import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { heroSlides } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5d2b1704-c912-40b5-94d1-19a432268dc7/artifacts/9p21esp1_logo-datasignz-creative-minds-%5BConverted%5D.png";
const RASTER_URL = "https://customer-assets.emergentagent.com/job_5d2b1704-c912-40b5-94d1-19a432268dc7/artifacts/k5lfkxbh_Raster.png";

const menuItems = [
  { nameKey: 'home', path: '/' },
  { nameKey: 'videoProduction', path: '/video-production' },
  { nameKey: 'postProduction', path: '/post-production' },
  { nameKey: 'onAirIdentity', path: '/on-air-identity' },
  { nameKey: 'marketingBranding', path: '/marketing-branding' },
  { nameKey: 'webServices', path: 'https://www.neuralnext.online/', external: true },
  { nameKey: 'contact', path: '/contact' }
];

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Header = () => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  
  // Memoize shuffled slides based on pathname - reshuffles when route changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shuffledSlides = useMemo(() => shuffleArray(heroSlides), [location.pathname]);
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [videosLoaded, setVideosLoaded] = useState({});
  const videoRefs = useRef([]);
  const langMenuRef = useRef(null);

  // Preload next video/image
  const preloadNextSlide = useCallback((index) => {
    const nextIndex = (index + 1) % shuffledSlides.length;
    const nextSlide = shuffledSlides[nextIndex];
    
    if (nextSlide.type === 'video' && !videosLoaded[nextIndex]) {
      // Preload video
      const video = document.createElement('video');
      video.preload = 'auto';
      video.src = nextSlide.media;
    } else if (nextSlide.type === 'image') {
      // Preload image
      const img = new Image();
      img.src = nextSlide.media;
    }
  }, [shuffledSlides, videosLoaded]);

  // Mark video as loaded
  const handleVideoLoaded = useCallback((index) => {
    setVideosLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };
    
    if (langMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [langMenuOpen]);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      const nextIndex = (prev + 1) % shuffledSlides.length;
      preloadNextSlide(nextIndex);
      return nextIndex;
    });
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, shuffledSlides.length, preloadNextSlide]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      const prevIndex = (prev - 1 + shuffledSlides.length) % shuffledSlides.length;
      preloadNextSlide(prevIndex);
      return prevIndex;
    });
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, shuffledSlides.length, preloadNextSlide]);

  // Preload first few slides on mount
  useEffect(() => {
    // Preload first 3 slides for faster initial experience
    shuffledSlides.slice(0, 3).forEach((slide) => {
      if (slide.type === 'image') {
        const img = new Image();
        img.src = slide.media;
      }
      // Preload video posters
      if (slide.poster) {
        const posterImg = new Image();
        posterImg.src = slide.poster;
      }
    });
  }, [shuffledSlides]);

  useEffect(() => {
    // Only auto-advance for images, not for videos
    const currentMedia = shuffledSlides[currentSlide];
    if (currentMedia && currentMedia.type === 'image') {
      const interval = setInterval(nextSlide, 6000);
      return () => clearInterval(interval);
    }
  }, [currentSlide, shuffledSlides, nextSlide]);

  // Handle video playback when slide changes or after shuffle
  useEffect(() => {
    // Small delay to ensure DOM is updated after shuffle
    const timer = setTimeout(() => {
      videoRefs.current.forEach((video, index) => {
        if (video) {
          if (index === currentSlide) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [currentSlide, shuffledSlides]);

  const currentProject = shuffledSlides[currentSlide];

  return (
    <header className="relative w-full min-h-[60vh] md:h-screen overflow-hidden bg-[#0b004c]">
      {/* Background Media (Images and Videos) */}
      {shuffledSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-500 flex items-center justify-center ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {slide.type === 'video' ? (
            <video
              ref={el => videoRefs.current[index] = el}
              src={index <= currentSlide + 1 || index === 0 ? slide.media : undefined}
              className="w-full h-full object-contain md:object-cover"
              muted
              loop
              playsInline
              autoPlay={index === currentSlide}
              preload={index === currentSlide ? 'auto' : 'metadata'}
              poster={slide.poster}
              onLoadedData={() => handleVideoLoaded(index)}
            />
          ) : (
            <img
              src={slide.media}
              alt={slide.projectName}
              className="w-full h-full object-contain md:object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          )}
        </div>
      ))}

      {/* Raster Overlay */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          backgroundImage: `url(${RASTER_URL})`,
          backgroundRepeat: 'repeat',
          opacity: 0.5
        }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/30 z-10" />

      {/* Logo */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-30">
        <Link to="/">
          <img
            src={LOGO_URL}
            alt="Datasignz CreativeMinds"
            className="h-6 md:h-8 w-auto"
          />
        </Link>
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white flex items-center justify-center transition-all duration-300 hover:bg-white/20"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white flex items-center justify-center transition-all duration-300 hover:bg-white/20"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 md:w-8 md:h-8 text-white" />
      </button>

      {/* Project Info */}
      <div className="absolute bottom-40 left-1/2 transform -translate-x-1/2 z-30 text-center">
        <h2 className="font-montserrat-bold text-white text-xs md:text-sm tracking-wider">
          <span className="font-bold">{currentProject.nameKey ? t(currentProject.nameKey) : currentProject.projectName}</span>
          {(() => {
            const desc = currentProject.descKey ? t(currentProject.descKey) : currentProject.projectDesc;
            // Only show description if it exists and isn't the key itself (fallback)
            if (desc && desc !== currentProject.descKey && desc.trim() !== '') {
              return <>: <span className="font-normal">{desc}</span></>;
            }
            return null;
          })()}
        </h2>
      </div>

      {/* Navigation Menu with Language Switcher */}
      <nav className="absolute bottom-0 left-0 right-0 z-30 bg-[#0b004c] mt-8">
        <div className="py-4">
          <ul className="flex flex-wrap justify-center items-center gap-x-2 md:gap-x-4 px-4">
            {menuItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <li>
                  {item.external ? (
                    <a
                      href={item.path}
                      className="font-montserrat-bold text-xs md:text-sm tracking-wider transition-colors duration-300 text-gray-500 hover:text-[#1a1a8a]"
                    >
                      {t(item.nameKey)}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`font-montserrat-bold text-xs md:text-sm tracking-wider transition-colors duration-300 ${
                        location.pathname === item.path
                          ? 'text-white'
                          : 'text-white/80 hover:text-[#6a6a6a]'
                      }`}
                    >
                      {t(item.nameKey)}
                    </Link>
                  )}
                </li>
                {index < menuItems.length - 1 && (
                  <li className="text-white/60 hidden md:block">|</li>
                )}
              </React.Fragment>
            ))}
            {/* Language Switcher in Menu Bar */}
            <li className="text-white/60 hidden md:block">|</li>
            <li className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 text-white/80 hover:text-white transition-colors font-montserrat-bold text-xs md:text-sm"
                data-testid="language-switcher-btn"
              >
                <Globe className="w-4 h-4" />
                <span>{languages.find(l => l.code === language)?.flag}</span>
              </button>
              
              {langMenuOpen && (
                <div 
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-[#0b004c] border border-white/20 rounded-lg shadow-lg overflow-hidden min-w-[150px]"
                  style={{ zIndex: 9999 }}
                  data-testid="language-dropdown"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm font-montserrat transition-colors ${
                        language === lang.code
                          ? 'bg-white/20 text-white'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </li>
          </ul>
        </div>
      </nav>

      {/* Slide Indicators */}
      <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
        {shuffledSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </header>
  );
};

export default Header;
